import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import React, { useState, useCallback, useRef } from 'react';
import { SyncOutlined } from '@ant-design/icons';
import type { Task } from '../lib/api';
import { updateTask } from '../lib/api';
import { useTaskStore } from '../stores/useTaskStore';
import { useDismissibleLayer } from '../hooks/useDismiss';
import { STATUS_COLUMNS } from '../lib/taskStatus';

interface TaskPillData {
  id: string;
  seq: number;
  status: Task['status'];
}

interface Props {
  task: TaskPillData;
  assigneeName?: string;
  showUnassigned?: boolean;
}

export default function TaskStatusPill({ task, assigneeName, showUnassigned = false }: Props) {
  const { i18n } = useLingui();
  const [menuOpen, setMenuOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const closeMenu = useCallback(() => setMenuOpen(false), []);
  useDismissibleLayer({
    active: menuOpen,
    within: ref,
    onEscape: closeMenu,
    onOutsidePress: closeMenu,
  });

  const handleStatusChange = useCallback(async (status: Task['status']) => {
    setMenuOpen(false);
    if (status === task.status) return;
    try {
      const updated = await updateTask(task.id, { status });
      useTaskStore.getState().upsertTask(updated);
    } catch { /* ignore */ }
  }, [task.id, task.status]);

  return (
    <div className="task-status-pill-wrapper" ref={ref}>
      <span
        className={`message-task-pill message-task-status-${task.status}`}
        onClick={e => { e.stopPropagation(); setMenuOpen(!menuOpen); }}
        title={i18n._(msg`Task ID: ${task.id} · Status: ${i18n._(STATUS_COLUMNS.find(column => column.key === task.status)!.label)}`)}
      >
        <span className="sr-only">{i18n._(STATUS_COLUMNS.find(column => column.key === task.status)!.label)}</span>
        {task.status === 'in_progress' ? <SyncOutlined aria-hidden="true" spin /> : STATUS_COLUMNS.find(s => s.key === task.status)?.icon} #{task.seq} {assigneeName ? `@${assigneeName}` : showUnassigned ? i18n._(msg`Unassigned`) : ''}
      </span>
      {menuOpen && (
        <div className="task-status-menu">
          {STATUS_COLUMNS.map(s => (
            <div
              key={s.key}
              className={`task-status-menu-item${s.key === task.status ? ' active' : ''}`}
              onClick={e => { e.stopPropagation(); handleStatusChange(s.key as Task['status']); }}
            >
              <span style={{ color: s.color }}>{s.icon}</span> {i18n._(s.label)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
